import { Loader2, TrendingUp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useEarnForm, type EarnMode } from '@/features/earn/hooks/use-earn-form'
import { cn } from '@/shared/ui/cn'

interface EarnModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

// fallow-ignore-next-line complexity
export function EarnModal({ open, onOpenChange }: EarnModalProps) {
  const form = useEarnForm(open)

  const submitDisabled =
    !form.canOperate ||
    !form.amountValid ||
    form.insufficientDeposit ||
    form.insufficientWithdraw ||
    form.vaultError

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" aria-hidden />
            Earn
          </DialogTitle>
          <DialogDescription>
            Deposit USDC into a third-party yield vault. Yield is not guaranteed and is generated by
            an independent DeFi protocol, not this app.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {form.vaultLoading ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              Loading vault…
            </p>
          ) : form.vaultError || !form.vault ? (
            <p className="text-sm text-destructive">
              Could not load Earn vault. Check server Privy credentials.
            </p>
          ) : (
            <div className="space-y-1 rounded-md border border-border px-3 py-2 text-sm">
              <div className="flex justify-between gap-2">
                <span className="text-muted-foreground">Vault</span>
                <span className="font-medium text-right">{form.vault.name}</span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-muted-foreground">APY</span>
                <span className="font-medium">{form.apyLabel}</span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-muted-foreground">In vault</span>
                <span className="font-medium">
                  {form.positionLoading
                    ? '…'
                    : `${form.position?.assetsInVaultFormatted ?? '0'} ${form.vault.asset.symbol.toUpperCase()}`}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-muted-foreground">Smart wallet USDC</span>
                <span className="font-medium">{form.scaUsdcFormatted}</span>
              </div>
            </div>
          )}

          {!form.smartReady && (
            <p className="text-xs text-muted-foreground">Smart wallet must be ready to use Earn.</p>
          )}

          {!form.onVaultChain && form.vaultChainId !== null && (
            <div className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-xs">
              <span>Switch to the vault network to continue.</span>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="h-7"
                onClick={() => void form.ensureVaultChain()}
                disabled={form.busy}
              >
                Switch
              </Button>
            </div>
          )}

          <Tabs value={form.mode} onValueChange={(value) => form.setMode(value as EarnMode)}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="deposit" disabled={form.busy}>
                Deposit
              </TabsTrigger>
              <TabsTrigger value="withdraw" disabled={form.busy}>
                Withdraw
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label htmlFor="earn-amount" className="text-sm font-medium">
                Amount (USDC)
              </label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={form.handleMax}
                disabled={form.busy}
              >
                Max
              </Button>
            </div>
            <Input
              id="earn-amount"
              type="number"
              min="0"
              step="0.01"
              placeholder="0.00"
              value={form.amountInput}
              onChange={(e) => form.setAmountInput(e.target.value)}
              disabled={form.busy}
            />
            {form.insufficientDeposit && (
              <p className="text-xs text-destructive">Insufficient USDC in smart wallet.</p>
            )}
            {form.insufficientWithdraw && (
              <p className="text-xs text-destructive">Amount exceeds vault balance.</p>
            )}
          </div>

          {form.statusMessage && (
            <p className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              {form.statusMessage}
            </p>
          )}

          <Button
            type="button"
            className={cn('w-full')}
            disabled={submitDisabled}
            onClick={() => void form.submit()}
          >
            {form.busy ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                Working…
              </>
            ) : form.mode === 'deposit' ? (
              'Deposit'
            ) : (
              'Withdraw'
            )}
          </Button>

          <p className="text-[11px] leading-snug text-muted-foreground">
            Deposits move USDC from your smart wallet to your Privy wallet, then into the vault.
            Withdrawals return funds to your smart wallet after the vault redeems.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}
